// PATH: src/context/ReferentialContext.tsx
import React, {
    useState,
    useEffect,
    createContext,
    useContext,
    useCallback,
    useRef,
} from 'react';
import { useReferentialApi } from '../api/referential/referential';
import { TCodification, TCountry } from '../models/referential/referential';
import { useAuth } from './AuthContext'; // 👈 on réagit au statut d'auth

const ReferentialContext = createContext<{
    isReferentialLoaded: boolean;
    codifications: TCodification[];
    countries: TCountry[];
    getCodificationIdByCodes: (codeType: string, code: string) => number;
    getCodificationByCodes: (codeType: string, code: string) => TCodification | undefined;
    getCodificationById: (id: number) => TCodification | undefined;
    getCodificationsByCodes: (codeType: string, codes: string[]) => TCodification[];
    getCodificationCodeById: (id: number) => string | undefined;
    getCodificationsByCodeType: (codeType: string) => TCodification[];
}>({
    isReferentialLoaded: false,
    codifications: [],
    countries: [],
    getCodificationIdByCodes: () => -1,
    getCodificationByCodes: () => undefined,
    getCodificationById: () => undefined,
    getCodificationsByCodes: () => [],
    getCodificationCodeById: () => undefined,
    getCodificationsByCodeType: () => [],
});

export const useReferential = () => useContext(ReferentialContext);

export const ReferentialProvider = ({ children }: { children: React.ReactNode }) => {
    const api = useReferentialApi();
    const apiRef = useRef(api);            // 👈 stabilise les méthodes API
    useEffect(() => { apiRef.current = api; }, [api]);

    const { status } = useAuth(); // "unknown" | "authenticated" | "anonymous"

    const [codifications, setCodifications] = useState<TCodification[]>([]);
    const [countries, setCountries] = useState<TCountry[]>([]);
    const [isReferentialLoaded, setIsReferentialLoaded] = useState<boolean>(false);

    const fetchingRef = useRef(false);     // 👈 anti double-fetch

    const todayISO = () => new Date().toISOString().split('T')[0];

    const loadFromLocalStorage = useCallback((): boolean => {
        const storedCodifications = localStorage.getItem('codifications');
        const storedCountries = localStorage.getItem('countries');
        const storedDate = localStorage.getItem('referentialDate');

        if (storedCodifications && storedCountries && storedDate === todayISO()) {
            setCodifications(JSON.parse(storedCodifications));
            setCountries(JSON.parse(storedCountries));
            setIsReferentialLoaded(true);
            return true;
        }
        return false;
    }, []);

    const persistToLocalStorage = (codi: TCodification[], ctry: TCountry[]) => {
        localStorage.setItem('codifications', JSON.stringify(codi));
        localStorage.setItem('countries', JSON.stringify(ctry));
        localStorage.setItem('referentialDate', todayISO());
    };

    const fetchData = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            const [codificationsData, countriesData] = await Promise.all([
                apiRef.current.getCodifications(),
                apiRef.current.getCountries(),
            ]);
            setCodifications(codificationsData);
            setCountries(countriesData);
            persistToLocalStorage(codificationsData, countriesData);
            setIsReferentialLoaded(true);
        } catch (error) {
            console.error('Failed to fetch referential data:', error);
            // on garde isReferentialLoaded=false pour réessayer plus tard
        } finally {
            fetchingRef.current = false;
        }
    }, []);

    // Retry fetch with backoff until both arrays are non-empty (bounded attempts)
    const fetchWithRetry = useCallback(async () => {
        if (isReferentialLoaded && codifications.length > 0 && countries.length > 0) return;
        const maxAttempts = 3;
        let attempt = 0;
        while (attempt < maxAttempts && !(codifications.length > 0 && countries.length > 0)) {
            // eslint-disable-next-line no-await-in-loop
            await fetchData();
            if (codifications.length > 0 && countries.length > 0) break;
            attempt += 1;
            const delay = Math.min(2000 * Math.pow(2, attempt - 1), 8000);
            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => setTimeout(res, delay));
        }
    }, [codifications.length, countries.length, fetchData, isReferentialLoaded]);

    // 1) Hydrate depuis le cache au montage (si valide)
    useEffect(() => {
        loadFromLocalStorage();
    }, [loadFromLocalStorage]);

    // 2) Charger les données au besoin, quel que soit le statut d'authentification.
    //    Les endpoints référentiels sont publics: il est utile de les rendre
    //    disponibles aussi sur les écrans "anonymes" (ex: inscription).
    useEffect(() => {
        const hasFreshCache = loadFromLocalStorage();
        if (!hasFreshCache) {
            void fetchWithRetry();
        }
    }, [status, loadFromLocalStorage, fetchWithRetry]);

    // 2.b) Si le cache est invalide (listes vides), relancer avec retry
    useEffect(() => {
        if (isReferentialLoaded && (codifications.length === 0 || countries.length === 0)) {
            setIsReferentialLoaded(false);
            void fetchWithRetry();
        }
    }, [isReferentialLoaded, codifications.length, countries.length, fetchWithRetry]);

    // 3) Synchronisation multi-onglets
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (!e.key) return;
            if (e.key === 'codifications' || e.key === 'countries' || e.key === 'referentialDate') {
                loadFromLocalStorage();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [loadFromLocalStorage]);

    // ---------- getters ----------
    const getCodificationIdByCodes = (codeType: string, code: string) => {
        const codification = codifications.find((c) => c.code_type === codeType && c.code === code);
        return codification ? codification.id : -1;
    };

    const getCodificationByCodes = (codeType: string, code: string): TCodification | undefined =>
        codifications.find((c) => c.code_type === codeType && c.code === code);

    const getCodificationById = (id: number): TCodification | undefined =>
        codifications.find((c) => c.id === id);

    const getCodificationsByCodes = (codeType: string, codes: string[]): TCodification[] =>
        codifications.filter((c) => c.code_type === codeType && codes.includes(c.code));

    // Id inconnu → undefined, jamais un code de remplacement : l'ancien repli
    // 'starting_box' affichait un autre bloc que celui enregistré. Relevé UAT
    // du 17/10 : 0 id inconnu sur 976 blocs et 2 090 leçons, aucun appelant
    // n'en dépendait. Avant chargement du référentiel, l'absence est normale.
    const getCodificationCodeById = (id: number): string | undefined => {
        const codification = codifications.find((c) => c.id === id);
        if (!codification && isReferentialLoaded) {
            console.error(`[Referential] Codification inconnue : id=${id}`);
        }
        return codification?.code;
    };

    const getCodificationsByCodeType = (codeType: string): TCodification[] =>
        codifications.filter((c) => c.code_type === codeType);

    return (
        <ReferentialContext.Provider
            value={{
                isReferentialLoaded,
                codifications,
                countries,
                getCodificationIdByCodes,
                getCodificationById,
                getCodificationByCodes,
                getCodificationsByCodes,
                getCodificationsByCodeType,
                getCodificationCodeById,
            }}
        >
            {children}
        </ReferentialContext.Provider>
    );
};
